import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import {
  Plus,
  Building2,
  MoreHorizontal,
  Edit,
  Trash2,
  Users,
  Phone,
  Mail,
  MapPin,
  Loader2,
  Shield
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const Agencies = () => {
  const { canManageAgencies } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    address: "",
    phone: "",
    email: "",
    manager_user_id: "",
    channel: ""
  });

  // Iter62m — tipul agenției (canal): clasificare + mapare automată a agenților
  const CHANNEL_OPTIONS = [
    { key: "d2d", label: "D2D" },
    { key: "telesales", label: "Telesales" },
    { key: "iasi", label: "Shop Iași" },
    { key: "bacau", label: "Shop Bacău" },
    { key: "cluj", label: "Shop Cluj" },
    { key: "oradea", label: "Shop Oradea" },
  ];
  const channelLabel = (ch) => CHANNEL_OPTIONS.find((o) => o.key === ch)?.label || null;
  const channelBadgeCls = (ch) =>
    ch === "d2d" ? "bg-indigo-100 text-indigo-700 border-indigo-200"
    : ch === "telesales" ? "bg-cyan-100 text-cyan-700 border-cyan-200"
    : ch ? "bg-amber-100 text-amber-700 border-amber-200"
    : "bg-slate-100 text-slate-500 border-slate-200";

  const fetchAgencies = async () => {
    try {
      const [agRes, uRes] = await Promise.all([
        axios.get(`${API_URL}/agencies`),
        axios.get(`${API_URL}/users`).catch(() => ({ data: [] })),
      ]);
      setAgencies(agRes.data);
      setUsers(uRes.data || []);
    } catch (error) {
      if (!error?.suppressToast) {
        toast.error("Eroare la încărcarea agențiilor", { id: "fetch-agencies" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgencies();
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      address: "",
      phone: "",
      email: "",
      manager_user_id: "",
      channel: ""
    });
  };

  const openEditDialog = (agency) => {
    setFormData({
      name: agency.name || "",
      code: agency.code || "",
      address: agency.address || "",
      phone: agency.phone || "",
      email: agency.email || "",
      manager_user_id: agency.manager_user_id || "",
      channel: agency.channel || ""
    });
    setEditDialog(agency.id);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.code) {
      toast.error("Numele și codul sunt obligatorii");
      return;
    }
    
    setSaving(true);
    try {
      const payload = { ...formData, manager_user_id: formData.manager_user_id || null };
      await axios.post(`${API_URL}/agencies`, payload);
      toast.success("Agenție creată cu succes");
      setCreateDialog(false);
      resetForm();
      fetchAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la crearea agenției");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name) {
      toast.error("Numele este obligatoriu");
      return;
    }
    
    setSaving(true);
    try {
      const payload = { ...formData, manager_user_id: formData.manager_user_id || null };
      await axios.put(`${API_URL}/agencies/${editDialog}`, payload);
      toast.success("Agenție actualizată");
      setEditDialog(null);
      resetForm();
      fetchAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la actualizarea agenției");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/agencies/${deleteDialog}`);
      toast.success("Agenție ștearsă");
      setDeleteDialog(null);
      fetchAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la ștergerea agenției");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Agenții</h1>
          <p className="text-slate-500 mt-1">
            Gestionează agențiile și echipele de vânzări
          </p>
        </div>
        {canManageAgencies() && (
          <Button
            className="bg-slate-900 hover:bg-slate-800"
            onClick={() => setCreateDialog(true)}
            data-testid="new-agency-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Agenție Nouă
          </Button>
        )}
      </div>

      {/* Agencies Grid */}
      {agencies.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Building2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Nu există agenții</h3>
              <p className="text-slate-500 mb-4">Creează prima agenție pentru a începe</p>
              {canManageAgencies() && (
                <Button onClick={() => setCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adaugă Agenție
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {agencies.map((agency) => (
            <Card key={agency.id} className="hover:shadow-md transition-shadow" data-testid={`agency-${agency.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{agency.name}</CardTitle>
                      <Badge variant="outline" className="mt-1">{agency.code}</Badge>
                    </div>
                  </div>
                  {canManageAgencies() && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(agency)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Editează
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600"
                          onClick={() => setDeleteDialog(agency.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Șterge
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${channelBadgeCls(agency.channel)}`}
                    data-testid={`agency-channel-${agency.id}`}
                    title="Tipul agenției — agenții alocați figurează automat pe acest canal în obiective și rapoarte"
                  >
                    {channelLabel(agency.channel) || "Fără tip"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Users className="h-4 w-4" />
                  <span data-testid={`agency-agents-count-${agency.id}`}>{agency.agents_count || 0} agenți</span>
                </div>
                {agency.manager && (
                  <div className="flex items-center gap-2 text-sm text-slate-600" data-testid={`agency-manager-${agency.id}`}>
                    <Shield className="h-4 w-4 text-amber-600" />
                    <span className="truncate">Manager: {agency.manager.name || agency.manager.email}</span>
                  </div>
                )}
                {agency.address && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MapPin className="h-4 w-4" />
                    <span className="truncate">{agency.address}</span>
                  </div>
                )}
                {agency.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="h-4 w-4" />
                    <span>{agency.phone}</span>
                  </div>
                )}
                {agency.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="h-4 w-4" />
                    <span className="truncate">{agency.email}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agenție Nouă</DialogTitle>
            <DialogDescription>
              Creează o nouă agenție de vânzări
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nume *</Label>
              <Input
                id="name"
                placeholder="Ex: București Nord"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="agency-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Cod *</Label>
              <Input
                id="code"
                placeholder="Ex: BUC-N"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                data-testid="agency-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Adresă</Label>
              <Input
                id="address"
                placeholder="Adresa agenției"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  placeholder="0721..."
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@agentie.ro"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Manager (User)</Label>
              <Select
                value={formData.manager_user_id || "none"}
                onValueChange={(v) => setFormData({ ...formData, manager_user_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="agency-manager-select">
                  <SelectValue placeholder="— fără manager —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— fără manager —</SelectItem>
                  {users.filter(u => ["admin", "backoffice", "manager"].includes(u.role)).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Managerul vede doar datele agențiilor pe care le coordonează.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Tip agenție (canal)</Label>
              <Select
                value={formData.channel || "none"}
                onValueChange={(v) => setFormData({ ...formData, channel: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="agency-channel-select">
                  <SelectValue placeholder="— fără tip —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— fără tip —</SelectItem>
                  {CHANNEL_OPTIONS.map((o) => (
                    <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Agenții alocați acestei agenții figurează automat pe acest canal în obiective și rapoarte.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialog(false); resetForm(); }}>
              Anulează
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-agency-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Creează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => { setEditDialog(null); resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editează Agenție</DialogTitle>
            <DialogDescription>
              Modifică detaliile agenției
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nume *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-code">Cod</Label>
              <Input
                id="edit-code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Adresă</Label>
              <Input
                id="edit-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Telefon</Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Manager (User)</Label>
              <Select
                value={formData.manager_user_id || "none"}
                onValueChange={(v) => setFormData({ ...formData, manager_user_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="edit-agency-manager-select">
                  <SelectValue placeholder="— fără manager —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— fără manager —</SelectItem>
                  {users.filter(u => ["admin", "backoffice", "manager"].includes(u.role)).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tip agenție (canal)</Label>
              <Select
                value={formData.channel || "none"}
                onValueChange={(v) => setFormData({ ...formData, channel: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="edit-agency-channel-select">
                  <SelectValue placeholder="— fără tip —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— fără tip —</SelectItem>
                  {CHANNEL_OPTIONS.map((o) => (
                    <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialog(null); resetForm(); }}>
              Anulează
            </Button>
            <Button onClick={handleUpdate} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmare Ștergere</DialogTitle>
            <DialogDescription>
              Ești sigur că vrei să ștergi această agenție? Această acțiune nu poate fi anulată.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              Anulează
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-agency">
              Șterge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Agencies;
